import { useEffect, useState, useContext, useRef } from "react";
import api from "../api/axios";
import { AuthContext } from "../context/AuthContext";
import {
  Box, Typography, Paper, Button, Divider, Avatar, TextField,
  IconButton, Drawer, List, ListItem, ListItemIcon, ListItemText,
  Alert,
} from "@mui/material";
import FavoriteIcon from "@mui/icons-material/Favorite";
import PersonIcon from "@mui/icons-material/Person";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import HistoryIcon from "@mui/icons-material/History";
import ReplayIcon from "@mui/icons-material/Replay";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import SettingsIcon from "@mui/icons-material/Settings";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import LogoutIcon from "@mui/icons-material/Logout";
import BuildIcon from "@mui/icons-material/Build";
import DashboardIcon from "@mui/icons-material/Dashboard";
import PaymentsIcon from "@mui/icons-material/Payments";
import CloseIcon from "@mui/icons-material/Close";
import MenuIcon from "@mui/icons-material/Menu";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { useNavigate, useLocation } from "react-router-dom";
import { useWishlist } from "../context/WishlistContext";
import { useCart } from "../context/CartContext";
import LoadingScreen from "../components/LoadingScreen";
import { hasMedia } from "../utils/productMedia";
import { consumeReelPosition } from "../utils/reelRestore";
import ProductCard from "../components/ProductCard";
import OrderDetailsDashboard from "./OrderDetailsDashboard";
import MobileReelCard from "../components/MobileReelCard";
import { Swiper, SwiperSlide } from "swiper/react";
import { Mousewheel, Keyboard } from "swiper/modules";
import "swiper/css";
import { colors } from "../utils/theme";
import ProfilePage from "./ProfilePage";
import socket from "../socket";

const UtilityCard = ({ icon, title, onClick, children }) => (
  <Paper onClick={onClick} sx={{ p: 2.5, borderRadius: 1, border: "1px solid", borderColor: colors.outlineVariant, boxShadow: "0 2px 4px rgba(0,0,0,0.08)", cursor: onClick ? "pointer" : "default" }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
      {icon}
      <Typography sx={{ fontWeight: 600, fontSize: 16, color: colors.onSurface }}>{title}</Typography>
    </Box>
    {children}
  </Paper>
);

/* 
 * Renders a single product as its own clickable order row.
 * Multi-product orders (same or different vendor) each get a separate row.
 */
const OrderItemRow = ({ item, order, navigate }) => {
  const lastTracking = order?.tracking?.at(-1);
  const isDelivered = lastTracking?.key === "DELIVERED";
  const productId = item?.productId || "";
  return (
    <Box
      sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, alignItems: { xs: "flex-start", md: "center" }, gap: 2, p: { xs: 2, md: 2.5 }, "&:hover": { bgcolor: colors.surfaceContainerLow } }}
    >
      <Box
        onClick={() => navigate(`/customer/dashboard/orders/${order._id}?focus=${productId}`)}
        sx={{ width: 80, height: 80, flexShrink: 0, border: "1px solid", borderColor: colors.outlineVariant, borderRadius: 1, p: 0.5, bgcolor: colors.onPrimary, display: "flex", alignItems: "center", justifyContent: "center", cursor: productId ? "pointer" : "default", overflow: "hidden" }}
      >
        {item?.imageUrl ? <Box component="img" src={item.imageUrl} alt={item.title} sx={{ width: "100%", height: "100%", objectFit: "contain" }} /> : <Typography variant="caption" sx={{ color: colors.textMuted }}>No Image</Typography>}
      </Box>
      <Box sx={{ flexGrow: 1 }}>
        <Typography
          onClick={() => navigate(`/customer/dashboard/orders/${order._id}?focus=${productId}`)}
          sx={{ fontWeight: 600, fontSize: 14, color: colors.textMain, cursor: productId ? "pointer" : "default", wordBreak: "break-word", overflowWrap: "break-word", maxWidth: { xs: "30ch", md: "60ch" }, "&:hover": { color: productId ? colors.primary : "inherit" } }}
        >
          {item?.title || "Order Items"}
        </Typography>
        <Typography sx={{ fontSize: 12, color: colors.textMuted, mt: 0.5 }}>Order #{order._id?.slice(-8)}</Typography>
        <Typography sx={{ fontSize: 12, color: colors.textMuted }}>Purchased on {new Date(order.createdAt).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })}</Typography>
        <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, px: 1, py: 0.25, borderRadius: 10, bgcolor: isDelivered ? "rgba(56,142,60,0.1)" : "rgba(254,152,0,0.1)", border: "1px solid", borderColor: isDelivered ? "rgba(56,142,60,0.2)" : "rgba(254,152,0,0.2)", mt: 0.5 }}>
          <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: isDelivered ? colors.successGreen : colors.secondaryContainer }} />
          <Typography sx={{ fontSize: 10, fontWeight: 700, color: isDelivered ? colors.successGreen : colors.secondaryContainer }}>{lastTracking?.label || "Processing"}</Typography>
        </Box>
      </Box>
      <Box sx={{ display: "flex", flexDirection: { xs: "row", md: "column" }, alignItems: { xs: "center", md: "flex-end" }, gap: 1, width: { xs: "100%", md: "auto" }, justifyContent: { xs: "space-between", md: "flex-start" } }}>
        <Typography sx={{ fontWeight: 700, fontSize: 22, color: colors.textMain }}>₹{Number((item?.price || 0) * (item?.qty || 1)).toLocaleString("en-IN")}</Typography>
      </Box>
      <Box sx={{ display: "flex", flexDirection: { xs: "row", md: "column" }, gap: 1 }}>
        {lastTracking?.key === "VENDOR_REJECTED" || lastTracking?.key === "CANCELLED" ? (
          <Button variant="contained" size="small" onClick={() => navigate(`/customer/dashboard/orders/${order._id}?focus=${productId}`)} sx={{ bgcolor: "#ba1a1a", color: "#fff", fontSize: 11, fontWeight: 600, textTransform: "none", whiteSpace: "nowrap", minHeight: 28, "&:hover": { bgcolor: "#93000a" } }}>View Details</Button>
        ) : (
          <Button variant="contained" size="small" onClick={() => navigate(`/customer/dashboard/orders/${order._id}?focus=${productId}`)} sx={{ bgcolor: "#FFD814", color: "#000", fontSize: 11, fontWeight: 600, textTransform: "none", whiteSpace: "nowrap", minHeight: 28, "&:hover": { bgcolor: "#e6c512" } }}>{isDelivered ? "View Order" : "Track Order"}</Button>
        )}
        <Button variant="outlined" size="small" sx={{ borderColor: colors.outlineVariant, color: colors.onSurfaceVariant, fontSize: 12, fontWeight: 600, textTransform: "none", whiteSpace: "nowrap", "&:hover": { bgcolor: colors.surfaceContainer } }}>Invoice</Button>
      </Box>
    </Box>
  );
};

const renderSidebarContent = (sidebarItems, userName, user, navigate, ordersExpanded, setOrdersExpanded, logout, setMobileMenuOpen) => (
  <Box sx={{ width: 256, py: 2 }}>
    <Box sx={{ px: 3, pb: 2, mb: 1, display: "flex", alignItems: "center", gap: 2, borderBottom: "1px solid", borderColor: colors.outlineVariant }}>
      <Avatar src={user?.profilePic || ""} sx={{ bgcolor: colors.primary, width: 44, height: 44 }}>{!user?.profilePic && <PersonIcon />}</Avatar>
      <Box><Typography sx={{ fontWeight: 600, fontSize: 14, color: colors.textMain }}>Hello, {userName}</Typography></Box>
    </Box>
    <List sx={{ px: 1 }}>
      {sidebarItems.map((item) => {
        if (item.subItems) {
          const isParentActive = item.subItems.some((s) => s.active);
          return (
            <Box key={item.label}>
              <ListItem onClick={() => setOrdersExpanded(!ordersExpanded)} sx={{ borderRadius: 1, mb: 0.5, cursor: "pointer", bgcolor: isParentActive ? "#d9e2ff" : "transparent", borderRight: isParentActive ? "4px solid" : "4px solid transparent", borderColor: isParentActive ? colors.primary : "transparent", "&:hover": { bgcolor: colors.surfaceContainerHigh } }}>
                <ListItemIcon sx={{ minWidth: 36, color: isParentActive ? colors.primary : colors.onSurfaceVariant }}>{item.icon}</ListItemIcon>
                <ListItemText primary={item.label} sx={{ "& .MuiListItemText-primary": { fontSize: 14, fontWeight: 600, color: isParentActive ? "#001944" : colors.onSurfaceVariant } }} />
                <Typography sx={{ fontSize: 12, color: colors.onSurfaceVariant, transform: ordersExpanded ? "rotate(90deg)" : "none", transition: "transform 0.2s" }}>▶</Typography>
              </ListItem>
              {ordersExpanded && (
                <Box sx={{ position: "relative", ml: 3, pl: 2, borderLeft: "2px dashed", borderColor: colors.outlineVariant }}>
                  {item.subItems.map((sub) => (
                    <Box key={sub.label} sx={{ position: "relative" }}>
                      <Box sx={{ position: "absolute", top: 20, left: -17, width: 14, height: 2, borderTop: "2px dashed", borderColor: colors.outlineVariant }} />
                      <ListItem onClick={() => { sub.onClick(); setMobileMenuOpen(false); }} sx={{ pl: 1, borderRadius: 1, mb: 0.5, cursor: "pointer", bgcolor: sub.active ? "#d9e2ff" : "transparent", borderRight: sub.active ? "4px solid" : "4px solid transparent", borderColor: sub.active ? colors.primary : "transparent", "&:hover": { bgcolor: colors.surfaceContainerHigh } }}>
                        <ListItemIcon sx={{ minWidth: 28, color: sub.active ? colors.primary : colors.onSurfaceVariant }}>{sub.icon}</ListItemIcon>
                        <ListItemText primary={sub.label} sx={{ "& .MuiListItemText-primary": { fontSize: 13, fontWeight: 500, color: sub.active ? "#001944" : colors.onSurfaceVariant } }} />
                      </ListItem>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          );
        }
        return (
          <ListItem key={item.label} onClick={() => { item.onClick(); setMobileMenuOpen(false); }} sx={{ borderRadius: 1, mb: 0.5, cursor: "pointer", bgcolor: item.active ? "#d9e2ff" : "transparent", borderRight: item.active ? "4px solid" : "4px solid transparent", borderColor: item.active ? colors.primary : "transparent", "&:hover": { bgcolor: colors.surfaceContainerHigh } }}>
            <ListItemIcon sx={{ minWidth: 36, color: item.active ? colors.primary : colors.onSurfaceVariant }}>{item.icon}</ListItemIcon>
            <ListItemText primary={item.label} sx={{ "& .MuiListItemText-primary": { fontSize: 14, fontWeight: 600, color: item.active ? "#001944" : colors.onSurfaceVariant } }} />
          </ListItem>
        );
      })}
    </List>
    <Box sx={{ mt: "auto", pt: 3, px: 1, borderTop: "1px solid", borderColor: colors.outlineVariant, mx: 3 }}>
      <ListItem onClick={async () => { await logout(); navigate("/login"); setMobileMenuOpen(false); }} sx={{ borderRadius: 1, cursor: "pointer", "&:hover": { bgcolor: colors.surfaceContainerHigh } }}>
        <ListItemIcon sx={{ minWidth: 36, color: colors.onSurfaceVariant }}><LogoutIcon /></ListItemIcon>
        <ListItemText primary="Logout" sx={{ "& .MuiListItemText-primary": { fontSize: 14, fontWeight: 600, color: colors.onSurfaceVariant } }} />
      </ListItem>
    </Box>
  </Box>
);

const OrdersSection = ({ user, navigate, orderFilter, toggleWishlist, isWishlisted }) => {
  const { addToCart } = useCart();
  const [orders, setOrders] = useState([]);
  const [buyAgainProducts, setBuyAgainProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [buyAgainActiveIndex, setBuyAgainActiveIndex] = useState(0);
  const [buyAgainAudio, setBuyAgainAudio] = useState(false);
  const buyAgainSwiperRef = useRef(null);
  const [dateRange, setDateRange] = useState("30-days");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [appliedStart, setAppliedStart] = useState("");
  const [appliedEnd, setAppliedEnd] = useState("");
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("newest");

  const getDateParam = (range) => {
    const now = new Date();
    switch (range) {
      case "custom": {
        if (appliedStart || appliedEnd) {
          const startIso = appliedStart ? new Date(appliedStart).toISOString() : "";
          // End date should include the entire chosen day (23:59:59.999Z), not midnight,
          // otherwise same-day orders created after 00:00 UTC get excluded.
          let endIso = "";
          if (appliedEnd) {
            const end = new Date(appliedEnd);
            end.setDate(end.getDate() + 1);
            end.setMilliseconds(-1);
            endIso = end.toISOString();
          }
          return `&startDate=${startIso}&endDate=${endIso}`;
        }
        return "";
      }
      case "30-days": return `&startDate=${new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString()}`;
      case "3-months": return `&startDate=${new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000).toISOString()}`;
      case "6-months": return `&startDate=${new Date(now.getTime() - 180 * 24 * 60 * 60 * 1000).toISOString()}`;
      case "this-year": return `&startDate=${new Date(now.getFullYear(), 0, 1).toISOString()}`;
      default: return "";
    }
  };

  const fetchOrders = async (pageNum, append) => {
    if (!user?.username) return;
    try {
      let filterParam = "";
      if (orderFilter === "in-transit") filterParam = "&filter=in-transit";
      else if (orderFilter === "manufacturing") filterParam = "&filter=manufacturing";
      else if (orderFilter === "delivered") filterParam = "&filter=delivered";
      else if (orderFilter === "cancelled") filterParam = "&filter=cancelled";
      else if (orderFilter === "returned") filterParam = "&filter=returned";
      const dateParam = getDateParam(dateRange);
      const res = await api.get(`/orders/customer/${user.username}?page=${pageNum}&limit=20${filterParam}${dateParam}`);
      if (append) {
        setOrders((prev) => [...prev, ...(res.data.orders || [])]);
      } else {
        setOrders(res.data.orders || []);
      }
      setHasMore(res.data.hasMore || false);
    } catch {
      if (!append) setOrders([]);
    }
  };

  useEffect(() => {
    if (!user?.username) return;
    if (orderFilter === "buy-again") {
      api.get(`/orders/customer/${user.username}?type=buy-again`)
        .then((res) => setBuyAgainProducts((res.data.buyAgainProducts || []).filter(hasMedia)))
        .catch(() => setBuyAgainProducts([]))
        .finally(() => setLoading(false));
    } else {
      setPage(1);
      setLoading(true);
      fetchOrders(1, false).finally(() => setLoading(false));
    }
  }, [user?.username, orderFilter, dateRange, appliedStart, appliedEnd]);

  // Restore the exact watched buy-again reel after back-navigation from a product
  useEffect(() => {
    if (loading || orderFilter !== "buy-again" || !buyAgainProducts.length) return;
    const saved = consumeReelPosition("buy-again");
    if (!saved || !buyAgainSwiperRef.current?.slideTo) return;
    const byId = buyAgainProducts.findIndex((p) => String(p.productId) === String(saved.id));
    const target = byId >= 0
      ? byId
      : (Number.isInteger(saved.index) && saved.index >= 0 && saved.index < buyAgainProducts.length ? saved.index : -1);
    if (target < 0) return;
    buyAgainSwiperRef.current.slideTo(target, 0);
    setBuyAgainActiveIndex(target);
  }, [loading, orderFilter, buyAgainProducts]);

  const handleLoadMore = async () => {
    setPage(page + 1);
    setLoadingMore(true);
    await fetchOrders(page + 1, true);
    setLoadingMore(false);
  };

  if (loading) return <LoadingScreen message="Loading orders..." />;

  const headerLabel = orderFilter === "in-transit" ? "In Transit Orders" : orderFilter === "manufacturing" ? "Under Manufacturing" : orderFilter === "delivered" ? "Delivered Orders" : orderFilter === "cancelled" ? "Cancelled Orders" : orderFilter === "returned" ? "Returned Orders" : orderFilter === "buy-again" ? "Buy Again" : "All Orders";

  if (orderFilter === "buy-again") {
    if (buyAgainProducts.length === 0) {
      return <Paper sx={{ p: 4, textAlign: "center", borderRadius: 1 }}><Typography sx={{ color: colors.textMuted }}>No buy again items yet</Typography></Paper>;
    }
    return (
      <Paper sx={{ borderRadius: 1, overflow: "hidden", boxShadow: "0 2px 4px rgba(0,0,0,0.08)" }}>
        <Box sx={{ px: { xs: 2, md: 3 }, py: 2, borderBottom: "1px solid", borderColor: colors.outlineVariant }}>
          <Typography sx={{ fontWeight: 600, fontSize: 18, color: colors.onSurface }}>{headerLabel} ({buyAgainProducts.length})</Typography>
        </Box>
        <Box sx={{ p: 2 }}>
          {/* MOBILE: full-screen vertical swiper */}
          <Box sx={{ display: { xs: "block", sm: "none" }, position: "fixed", top: 0, left: 0, width: "100%", height: "100dvh", zIndex: 1200, bgcolor: "#000" }}>
            <Swiper direction="vertical" modules={[Mousewheel, Keyboard]} mousewheel={{ forceToAxis: true }} keyboard={{ enabled: true }}
              onSwiper={(swiper) => (buyAgainSwiperRef.current = swiper)}
              onSlideChange={(swiper) => setBuyAgainActiveIndex(swiper.activeIndex)}
              style={{ width: "100%", height: "100%", overflow: "hidden" }}>
              {buyAgainProducts.map((item, index) => (
                <SwiperSlide key={item.productId} style={{ height: "100%", margin: 0, padding: 0, overflow: "hidden" }}>
                  <MobileReelCard item={item} index={index} reelSource="buy-again" isActive={index === buyAgainActiveIndex}
                    onNavigate={navigate}
                    audioEnabled={buyAgainAudio}
                    onToggleAudio={() => setBuyAgainAudio(!buyAgainAudio)}
                    isWishlisted={isWishlisted(item.productId)}
                    onToggle={(p) => toggleWishlist({ id: p.productId, title: p.title, price: p.price, imageUrl: p.imageUrl })}
                    onBuyProduct={(id) => navigate(`/product/${id}`)} />
                </SwiperSlide>
              ))}
              <SwiperSlide key="end-of-list" style={{ height: "100%", margin: 0, padding: 0, overflow: "hidden" }}>
                <Box sx={{ height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", bgcolor: "#000", color: "#fff", p: 3 }}>
                  <Typography sx={{ fontSize: 48, mb: 2, opacity: 0.5 }}>🏁</Typography>
                  <Typography sx={{ fontWeight: 600, fontSize: 18, mb: 1 }}>You've reached the end</Typography>
                  <Typography sx={{ color: "rgba(255,255,255,0.6)", fontSize: 14, mb: 3 }}>No more products to show</Typography>
                  <Button variant="contained" onClick={() => navigate("/customer/dashboard")} sx={{ bgcolor: "#FFD814", color: "#000", textTransform: "none", fontWeight: 600, borderRadius: "100px", px: 4, py: 1, "&:hover": { bgcolor: "#e6c512" } }}>Go to Dashboard</Button>
                </Box>
              </SwiperSlide>
            </Swiper>
          </Box>

          {/* DESKTOP: grid layout */}
          <Box sx={{ display: { xs: "none", sm: "grid" }, gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 2 }}>
            {buyAgainProducts.map((item) => (
              <ProductCard key={item.productId} product={item} variant="buy-again"
                onNavigate={(id) => navigate(`/product/${id}`)}
                onAddToCart={(p) => addToCart({ id: item.productId, title: p.title, price: Number(String(p.price).replace(/,/g, "")) || 0, imageUrl: p.imageUrl || p.images?.[0]?.thumbnailUrl || "", qty: 1 })}
                onBuyProduct={(id) => navigate(`/product/${id}`)}
                onToggleFavorite={(p) => toggleWishlist({ id: p.productId, title: p.title, price: p.price, imageUrl: p.imageUrl })}
                isWishlisted={isWishlisted(item.productId)} />
            ))}
          </Box>
        </Box>
      </Paper>
    );
  }

  // Flatten orders into one row per product so search & sort operate at product level
  let rows = [];
  for (const order of orders) {
    if ((order.items || []).length) {
      for (const item of order.items) rows.push({ order, item });
    } else {
      rows.push({ order, item: { title: "Order Items" } });
    }
  }

  const term = search.trim().toLowerCase();
  if (term) {
    rows = rows.filter(({ order, item }) =>
      (item?.title || "").toLowerCase().includes(term) ||
      (order._id || "").toLowerCase().includes(term)
    );
  }

  if (sortBy === "oldest") {
    rows.sort((a, b) => new Date(a.order.createdAt) - new Date(b.order.createdAt));
  } else if (sortBy === "price-desc") {
    rows.sort((a, b) => {
      const pa = (a.item?.price || 0) * (a.item?.qty || 1);
      const pb = (b.item?.price || 0) * (b.item?.qty || 1);
      return (pb - pa) || (new Date(b.order.createdAt) - new Date(a.order.createdAt)); // date tie-break for deterministic order
    });
  } else if (sortBy === "price-asc") {
    rows.sort((a, b) => {
      const pa = (a.item?.price || 0) * (a.item?.qty || 1);
      const pb = (b.item?.price || 0) * (b.item?.qty || 1);
      return (pa - pb) || (new Date(b.order.createdAt) - new Date(a.order.createdAt)); // date tie-break for deterministic order
    });
  } else {
    rows.sort((a, b) => new Date(b.order.createdAt) - new Date(a.order.createdAt));
  }

  return (
    <Paper sx={{ borderRadius: 1, overflow: "hidden", boxShadow: "0 2px 4px rgba(0,0,0,0.08)" }}>
      <Box sx={{ px: { xs: 2, md: 3 }, py: 2, borderBottom: "1px solid", borderColor: colors.outlineVariant, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
        <Typography sx={{ fontWeight: 600, fontSize: 18, color: colors.onSurface }}>{headerLabel}</Typography>
        <Box component="select" value={dateRange} onChange={(e) => setDateRange(e.target.value)}
          sx={{ fontSize: 13, p: 0.5, borderRadius: 1, border: "1px solid", borderColor: colors.outlineVariant, bgcolor: colors.onPrimary, color: colors.onSurface, cursor: "pointer" }}>
          <option value="all">All Time</option>
          <option value="30-days">Last 30 Days</option>
          <option value="3-months">Last 3 Months</option>
          <option value="6-months">Last 6 Months</option>
          <option value="this-year">This Year</option>
          <option value="custom">Custom Range</option>
        </Box>
        {dateRange === "custom" && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%", mt: 0.5 }}>
            <TextField type="date" size="small" value={customStart} onChange={(e) => setCustomStart(e.target.value)}
              label="Start" InputLabelProps={{ shrink: true }} sx={{ width: 180, "& .MuiInputBase-root": { fontSize: 13 } }} />
            <TextField type="date" size="small" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)}
              label="End" InputLabelProps={{ shrink: true }} sx={{ width: 180, "& .MuiInputBase-root": { fontSize: 13 } }} />
            {customStart || customEnd ? (
              <Button size="small" onClick={() => { setPage(1); setAppliedStart(customStart); setAppliedEnd(customEnd); setLoading(true); fetchOrders(1, false).finally(() => setLoading(false)); }}
                sx={{ color: colors.primary, border: "1px solid", borderColor: colors.outlineVariant, textTransform: "none", fontWeight: 600, fontSize: 12 }}>Apply</Button>
            ) : null}
          </Box>
        )}
      </Box>
      <Box sx={{ px: { xs: 2, md: 3 }, py: 1.5, borderBottom: "1px solid", borderColor: colors.outlineVariant, display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
        <TextField
          size="small"
          placeholder="Search by product or order #..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flexGrow: 1, flexShrink: 1, minWidth: 160, "& .MuiInputBase-root": { fontSize: 13 } }}
        />
        <Box component="select" value={sortBy} onChange={(e) => setSortBy(e.target.value)}
          sx={{ fontSize: 13, p: 0.5, borderRadius: 1, border: "1px solid", borderColor: colors.outlineVariant, bgcolor: colors.onPrimary, color: colors.onSurface, cursor: "pointer" }}>
          <option value="newest">Newest First</option>
          <option value="oldest">Oldest First</option>
          <option value="price-desc">Price: High to Low</option>
          <option value="price-asc">Price: Low to High</option>
        </Box>
      </Box>
      <Box>
        {rows.length ? (
          rows.map((row, idx) => (
            <Box key={`${row.order._id}-${row.item?.productId || idx}`}>
              <OrderItemRow item={row.item} order={row.order} navigate={navigate} />
              {idx < rows.length - 1 && <Divider sx={{ borderColor: colors.outlineVariant, mx: { xs: 2, md: 3 } }} />}
            </Box>
          ))
        ) : (
          <Box sx={{ p: 3, textAlign: "center" }}><Typography sx={{ color: colors.textMuted, fontSize: 14 }}>No matching orders found</Typography></Box>
        )}
      </Box>
      {hasMore && !search.trim() && (
        <Box sx={{ p: 2, textAlign: "center" }}>
          <Button variant="outlined" onClick={handleLoadMore} disabled={loadingMore} sx={{ borderColor: colors.outlineVariant, color: colors.primary, textTransform: "none", fontWeight: 600, px: 4 }}>{loadingMore ? "Loading..." : "Load More"}</Button>
        </Box>
      )}
    </Paper>
  );
};

const WishlistSection = ({ navigate }) => {
  const { clearWishlist, toggleWishlist, isWishlisted } = useWishlist();
  const { addToCart } = useCart();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);
  const [wishlistAudio, setWishlistAudio] = useState(false);
  const wishlistSwiperRef = useRef(null);

  const handleToggleWishlist = (item) => {
    const id = item.id || item._id || item.productId;
    setItems((prev) => prev.filter((visibleItem) => (visibleItem.id || visibleItem._id || visibleItem.productId) !== id));
    toggleWishlist(item);
  };

  const handleClearWishlist = () => {
    setItems([]);
    clearWishlist();
  };

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    api.get("/wishlist").then((res) => {
      if (!mounted) return;
      const mapped = (res.data?.items || []).map((item) => ({
        ...item,
        id: item.id || item._id || item.productId,
        productId: item.productId || item.id || item._id,
      }));
      setItems(mapped);
    }).catch(() => { if (mounted) setItems([]); }).finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  // Restore the exact watched wishlist reel after back-navigation from a product
  useEffect(() => {
    if (loading || !items.length) return;
    const saved = consumeReelPosition("wishlist");
    if (!saved || !wishlistSwiperRef.current?.slideTo) return;
    const byId = items.findIndex((it) => String(it.id || it._id || it.productId) === String(saved.id));
    const target = byId >= 0
      ? byId
      : (Number.isInteger(saved.index) && saved.index >= 0 && saved.index < items.length ? saved.index : -1);
    if (target < 0) return;
    wishlistSwiperRef.current.slideTo(target, 0);
    setActiveIndex(target);
  }, [loading, items]);

  if (loading) return <LoadingScreen message="Loading wishlist..." />;

  if (!items.length) {
    return (
      <Paper sx={{ p: 4, textAlign: "center", borderRadius: 1 }}>
        <Box sx={{ fontSize: 60, mb: 2, opacity: 0.3 }}>🎬</Box>
        <Typography sx={{ color: colors.textMuted, mb: 1 }}>Your wishlist is empty</Typography>
        <Button variant="contained" onClick={() => navigate("/")} sx={{ bgcolor: colors.primary, color: colors.onPrimary, textTransform: "none", borderRadius: "100px", px: 4 }}>Browse Reels</Button>
      </Paper>
    );
  }

  return (
    <Paper sx={{ borderRadius: 1, overflow: "hidden", boxShadow: "0 2px 4px rgba(0,0,0,0.08)" }}>
      <Box sx={{ px: { xs: 2, md: 3 }, py: 2, display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid", borderColor: colors.outlineVariant }}>
        <Typography sx={{ fontWeight: 600, fontSize: 18, color: colors.onSurface }}>Saved Items ({items.length})</Typography>
        <Button size="small" onClick={handleClearWishlist} sx={{ color: colors.textMuted, textTransform: "none", fontSize: 13, "&:hover": { color: colors.error } }}>Clear all</Button>
      </Box>
      <Box sx={{ p: 2 }}>
        {/* MOBILE: full-screen vertical swiper */}
        <Box sx={{ display: { xs: "block", sm: "none" }, position: "fixed", top: 0, left: 0, width: "100%", height: "100dvh", zIndex: 1200, bgcolor: "#000" }}>
          <Swiper direction="vertical" modules={[Mousewheel, Keyboard]} mousewheel={{ forceToAxis: true }} keyboard={{ enabled: true }}
            onSwiper={(swiper) => (wishlistSwiperRef.current = swiper)}
            onSlideChange={(swiper) => setActiveIndex(swiper.activeIndex)}
            style={{ width: "100%", height: "100%", overflow: "hidden" }}>
            {items.map((item, index) => {
              const itemId = item.id || item._id || item.productId;
              return (
                <SwiperSlide key={itemId} style={{ height: "100%", margin: 0, padding: 0, overflow: "hidden" }}>
                  <MobileReelCard item={item} index={index} reelSource="wishlist" isActive={index === activeIndex}
                    onNavigate={navigate}
                    audioEnabled={wishlistAudio}
                    onToggleAudio={() => setWishlistAudio(!wishlistAudio)}
                    isWishlisted={true}
                    onAddToCart={(p) => addToCart({ id: itemId, title: p.title, price: Number(String(p.price).replace(/,/g, "")) || 0, imageUrl: p.imageUrl || p.images?.[0]?.thumbnailUrl || "", qty: 1 })}
                    onToggle={handleToggleWishlist}
                    onBuyProduct={(id) => navigate(`/product/${id}`)} />
                </SwiperSlide>
              );
            })}
            <SwiperSlide key="end-of-list" style={{ height: "100%", margin: 0, padding: 0, overflow: "hidden" }}>
              <Box sx={{ height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", bgcolor: "#000", color: "#fff", p: 3 }}>
                <Typography sx={{ fontSize: 48, mb: 2, opacity: 0.5 }}>🏁</Typography>
                <Typography sx={{ fontWeight: 600, fontSize: 18, mb: 1 }}>You've reached the end</Typography>
                <Typography sx={{ color: "rgba(255,255,255,0.6)", fontSize: 14, mb: 3 }}>No more products to show</Typography>
                <Button variant="contained" onClick={() => navigate("/customer/dashboard")} sx={{ bgcolor: "#FFD814", color: "#000", textTransform: "none", fontWeight: 600, borderRadius: "100px", px: 4, py: 1, "&:hover": { bgcolor: "#e6c512" } }}>Go to Dashboard</Button>
              </Box>
            </SwiperSlide>
          </Swiper>
        </Box>

        {/* DESKTOP: grid layout */}
        <Box sx={{ display: { xs: "none", sm: "grid" }, gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 2 }}>
          {items.map((item) => {
            const itemId = item.id || item._id || item.productId;
            return (
              <ProductCard key={itemId} product={item} variant="wishlist"
                onNavigate={(id) => navigate(`/product/${id}`)}
                onBuyProduct={(id) => navigate(`/product/${id}`)}
                onAddToCart={(p) => addToCart({ id: itemId, title: p.title, price: Number(String(p.price).replace(/,/g, "")) || 0, imageUrl: p.imageUrl || p.images?.[0]?.thumbnailUrl || "", qty: 1 })}
                onToggleFavorite={handleToggleWishlist}
                isWishlisted={isWishlisted(itemId)} />
            );
          })}
        </Box>
      </Box>
    </Paper>
  );
};

const BrowseHistorySection = ({ navigate }) => {
  const { addToCart } = useCart();
  const { toggleWishlist, isWishlisted } = useWishlist();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);
  const [audioEnabled, setAudioEnabled] = useState(false);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    const fetchProducts = async () => {
      try {
        const res = await api.get("/browse-history");
        if (!mounted) return;
        if (res.data.success) {
          setProducts((res.data.products || []).filter(hasMedia));
        } else {
          setProducts([]);
        }
      } catch {
        if (mounted) setProducts([]);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetchProducts();
    return () => { mounted = false; };
  }, []);

  const clearHistory = async () => {
    try {
      await api.delete("/browse-history");
    } catch {}
    setProducts([]);
  };

  const removeProduct = async (productId) => {
    try {
      await api.delete(`/browse-history/item/${productId}`);
    } catch {}
    setProducts(products.filter((p) => p._id !== productId));
  };

  if (loading) return <LoadingScreen message="Loading history..." />;

  if (!products.length) {
    return (
      <Paper sx={{ p: 4, textAlign: "center", borderRadius: 1 }}>
        <Box sx={{ fontSize: 60, mb: 2, opacity: 0.3 }}>📖</Box>
        <Typography sx={{ color: colors.textMuted, mb: 1 }}>No browsing history</Typography>
        <Typography sx={{ color: colors.textMuted, fontSize: 13, mb: 2 }}>Products you view will appear here</Typography>
        <Button variant="contained" onClick={() => navigate("/")} sx={{ bgcolor: "#FFD814", color: "#000", textTransform: "none", borderRadius: "100px", px: 4, "&:hover": { bgcolor: "#e6c512" } }}>Browse Reels</Button>
      </Paper>
    );
  }

  return (
    <Paper sx={{ borderRadius: 1, overflow: "hidden", boxShadow: "0 2px 4px rgba(0,0,0,0.08)" }}>
      <Box sx={{ px: { xs: 2, md: 3 }, py: 2, display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid", borderColor: colors.outlineVariant }}>
        <Typography sx={{ fontWeight: 600, fontSize: 18, color: colors.onSurface }}>Browsing History ({products.length})</Typography>
        <Button size="small" onClick={clearHistory} sx={{ color: colors.textMuted, textTransform: "none", fontSize: 13, "&:hover": { color: colors.error } }}>Clear all</Button>
      </Box>
      <Box sx={{ p: 2 }}>
        {/* MOBILE: full-screen vertical swiper */}
        <Box sx={{ display: { xs: "block", sm: "none" }, position: "fixed", top: 0, left: 0, width: "100%", height: "100dvh", zIndex: 1200, bgcolor: "#000" }}>
          <Swiper direction="vertical" modules={[Mousewheel, Keyboard]} mousewheel={{ forceToAxis: true }} keyboard={{ enabled: true }}
            onSlideChange={(swiper) => setActiveIndex(swiper.activeIndex)}
            style={{ width: "100%", height: "100%", overflow: "hidden" }}>
            {products.map((product, index) => (
              <SwiperSlide key={product._id} style={{ height: "100%", margin: 0, padding: 0, overflow: "hidden" }}>
                <MobileReelCard item={product} isActive={index === activeIndex}
                  onNavigate={navigate}
                  audioEnabled={audioEnabled}
                  onToggleAudio={() => setAudioEnabled(!audioEnabled)}
                  isWishlisted={isWishlisted(product._id)}
                  onAddToCart={(p) => addToCart({ id: p._id, title: p.title, price: Number(String(p.price).replace(/,/g, "")) || 0, imageUrl: p.imageUrl || p.images?.[0]?.thumbnailUrl || "", qty: 1 })}
                  onToggle={(p) => toggleWishlist({ id: p._id, title: p.title, price: p.price, imageUrl: p.imageUrl || p.images?.[0]?.thumbnailUrl, videoUrl: p.videoUrl, images: p.images })}
                  onBuyProduct={(id) => navigate(`/delivery-location/${id}`)} />
              </SwiperSlide>
            ))}
            <SwiperSlide key="end-of-list" style={{ height: "100%", margin: 0, padding: 0, overflow: "hidden" }}>
              <Box sx={{ height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", bgcolor: "#000", color: "#fff", p: 3 }}>
                <Typography sx={{ fontSize: 48, mb: 2, opacity: 0.5 }}>🏁</Typography>
                <Typography sx={{ fontWeight: 600, fontSize: 18, mb: 1 }}>You've reached the end</Typography>
                <Typography sx={{ color: "rgba(255,255,255,0.6)", fontSize: 14, mb: 3 }}>No more products to show</Typography>
                <Button variant="contained" onClick={() => navigate("/customer/dashboard")} sx={{ bgcolor: "#FFD814", color: "#000", textTransform: "none", fontWeight: 600, borderRadius: "100px", px: 4, py: 1, "&:hover": { bgcolor: "#e6c512" } }}>Go to Dashboard</Button>
              </Box>
            </SwiperSlide>
          </Swiper>
        </Box>

        {/* DESKTOP: grid layout */}
        <Box sx={{ display: { xs: "none", sm: "grid" }, gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 2 }}>
          {products.map((product) => (
            <ProductCard key={product._id} product={product} variant="browse-history"
              onNavigate={(id) => navigate(`/product/${id}`)}
              onBuyProduct={(id) => navigate(`/delivery-location/${id}`)}
              onRemove={(id) => removeProduct(id)}
              onAddToCart={(p) => addToCart({ id: p._id, title: p.title, price: Number(String(p.price).replace(/,/g, "")) || 0, imageUrl: p.imageUrl || p.images?.[0]?.thumbnailUrl || "", qty: 1 })}
              onToggleFavorite={(p) => toggleWishlist({ id: p._id, title: p.title, price: p.price, imageUrl: p.imageUrl || p.images?.[0]?.thumbnailUrl, videoUrl: p.videoUrl, images: p.images })}
              isWishlisted={isWishlisted(product._id)} />
          ))}
        </Box>
      </Box>
    </Paper>
  );
};

const CustomerDashboard = () => {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { wishlist, toggleWishlist, isWishlisted } = useWishlist();

  const pathAfterDashboard = location.pathname.replace("/customer/dashboard", "").replace(/^\//, "");
  const pathParts = pathAfterDashboard.split("/");
  const activeSection = pathParts[0] || "dashboard";
  const orderId = pathParts[0] === "orders" && pathParts[1] ? pathParts[1] : null;

  const [totalOrders, setTotalOrders] = useState(0);
  const [inTransitOrders, setInTransitOrders] = useState(0);
  const [manufacturingOrders, setManufacturingOrders] = useState(0);
  const [recentOrders, setRecentOrders] = useState([]);
  const [buyAgainTotal, setBuyAgainTotal] = useState(0);
  const [recentlyViewed, setRecentlyViewed] = useState([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // ponytail: persist only "buy-again" so back-navigation from a product lands
  // on the same tab; other filters reset on remount exactly as before.
  const [orderFilter, setOrderFilterState] = useState(() => {
    try { return sessionStorage.getItem("dashboard.orderFilter"); } catch { return null; }
  });
  const setOrderFilter = (value) => {
    setOrderFilterState(value);
    try {
      if (value === "buy-again") sessionStorage.setItem("dashboard.orderFilter", value);
      else sessionStorage.removeItem("dashboard.orderFilter");
    } catch {}
  };
  const [ordersExpanded, setOrdersExpanded] = useState(true);
  const [cancelNotification, setCancelNotification] = useState(null);

  useEffect(() => {
    if (!user?.username) return;
    api.get(`/orders/customer/${user.username}/dashboard`).then((res) => {
      setTotalOrders(res.data.totalOrders);
      setInTransitOrders(res.data.inTransitOrders || 0);
      setManufacturingOrders(res.data.manufacturingOrders || 0);
      const rawRecent = res.data.recentOrders || [];
      // Flatten groups into individual orders so customers always see order IDs, not group IDs
      const flattened = [];
      for (const entry of rawRecent) {
        if (entry.orders && Array.isArray(entry.orders)) {
          flattened.push(...entry.orders);
        } else {
          flattened.push(entry);
        }
      }
      setRecentOrders(flattened);
      setBuyAgainTotal(res.data.buyAgainTotal || 0);
    }).catch(() => {
      setTotalOrders(0); setInTransitOrders(0); setManufacturingOrders(0);
      setRecentOrders([]); setBuyAgainTotal(0);
    });
  }, [user?.username]);

  useEffect(() => {
    if (user) {
      // Fetch from backend for logged-in users
      api.get("/browse-history").then((res) => {
        setRecentlyViewed((res.data.products || []).slice(0, 3));
      }).catch(() => {});
    } else {
      // Fallback to localStorage for guests
      const ids = JSON.parse(localStorage.getItem("recentlyViewed")) || [];
      if (!ids.length) return;
      api.post("/products/recently-viewed", { ids: ids.slice(0, 3) }).then((res) => setRecentlyViewed(res.data.products || [])).catch(() => {});
    }
  }, [user]);

  // 📡 Listen for order cancellation notifications
  useEffect(() => {
    if (!user?._id) return;
    socket.emit("customer_join", user._id);

    const handleCancelled = (data) => {
      setCancelNotification(data);
    };

    socket.on("order_cancelled", handleCancelled);
    return () => socket.off("order_cancelled", handleCancelled);
  }, [user?._id]);

  const userName = user?.firstName || user?.username || "Customer";

  const sidebarItems = [
    { icon: <DashboardIcon sx={{ color: colors.primary }} />, label: "Dashboard", active: activeSection === "dashboard", onClick: () => navigate("/customer/dashboard") },
    { icon: <Inventory2Icon sx={{ color: colors.primary }} />, label: "My Orders", active: activeSection === "orders" && !orderFilter, onClick: () => { setOrderFilter(null); navigate("/customer/dashboard/orders"); },
      subItems: [
        { icon: <Inventory2Icon sx={{ color: colors.primary }} />, label: "All Orders", active: activeSection === "orders" && !orderFilter, onClick: () => { setOrderFilter(null); navigate("/customer/dashboard/orders"); } },
        { icon: <BuildIcon sx={{ color: colors.warningAmber }} />, label: "Under Manufacturing", active: activeSection === "orders" && orderFilter === "manufacturing", onClick: () => { setOrderFilter("manufacturing"); navigate("/customer/dashboard/orders"); } },
        { icon: <LocalShippingIcon sx={{ color: colors.secondaryContainer }} />, label: "In Transit", active: activeSection === "orders" && orderFilter === "in-transit", onClick: () => { setOrderFilter("in-transit"); navigate("/customer/dashboard/orders"); } },
        { icon: <CheckCircleIcon sx={{ color: colors.successGreen }} />, label: "Delivered", active: activeSection === "orders" && orderFilter === "delivered", onClick: () => { setOrderFilter("delivered"); navigate("/customer/dashboard/orders"); } },
        { icon: <CancelIcon sx={{ color: colors.error }} />, label: "Cancelled", active: activeSection === "orders" && orderFilter === "cancelled", onClick: () => { setOrderFilter("cancelled"); navigate("/customer/dashboard/orders"); } },
        { icon: <ReplayIcon sx={{ color: colors.warningAmber }} />, label: "Returned", active: activeSection === "orders" && orderFilter === "returned", onClick: () => { setOrderFilter("returned"); navigate("/customer/dashboard/orders"); } },
      ]
    },
    { icon: <FavoriteIcon sx={{ color: colors.error }} />, label: "Wishlist", active: activeSection === "wishlist", onClick: () => navigate("/customer/dashboard/wishlist") },
    { icon: <ReplayIcon sx={{ color: colors.successGreen }} />, label: "Buy Again", active: activeSection === "orders" && orderFilter === "buy-again", onClick: () => { setOrderFilter("buy-again"); navigate("/customer/dashboard/orders"); } },
    { icon: <SettingsIcon sx={{ color: colors.onSurfaceVariant }} />, label: "Account Settings", active: activeSection === "settings", onClick: () => navigate("/customer/dashboard/settings") },
    { icon: <SupportAgentIcon sx={{ color: colors.secondaryContainer }} />, label: "Support", active: activeSection === "support", onClick: () => navigate("/customer/dashboard/support") },
  ];

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: colors.surfaceBright }}>
      <Drawer anchor="left" open={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)}>
        <Box sx={{ display: "flex", justifyContent: "flex-end", p: 1 }}><IconButton onClick={() => setMobileMenuOpen(false)}><CloseIcon /></IconButton></Box>
        {renderSidebarContent(sidebarItems, userName, user, navigate, ordersExpanded, setOrdersExpanded, logout, setMobileMenuOpen)}
      </Drawer>
      <Box sx={{ display: "flex", gap: 2, px: { xs: 1.5, md: 3 }, py: { xs: 2, md: 3 } }}>
        <Box sx={{ display: { xs: "none", md: "block" }, flexShrink: 0 }}>
          <Paper sx={{ width: 256, position: "sticky", top: 80, borderRadius: 1, boxShadow: "0 2px 4px rgba(0,0,0,0.08)" }}>
            {renderSidebarContent(sidebarItems, userName, user, navigate, ordersExpanded, setOrdersExpanded, logout, setMobileMenuOpen)}
          </Paper>
        </Box>
        <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
          <Box sx={{ display: { xs: "block", md: "none" } }}>
            <IconButton onClick={() => setMobileMenuOpen(true)} sx={{ color: colors.onSurfaceVariant, border: "1px solid", borderColor: colors.outlineVariant, borderRadius: 1, p: 1 }}>
              <MenuIcon />
            </IconButton>
          </Box>
          {cancelNotification && (
            <Alert severity="error" onClose={() => setCancelNotification(null)} sx={{ mb: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <span>{cancelNotification.message || "An order has been cancelled by the vendor"}</span>
                  {cancelNotification.orderId && cancelNotification.orderId !== orderId && (
                  <Button
                    size="small"
                    variant="contained"
                    sx={{ bgcolor: "#ba1a1a", color: "#fff", textTransform: "none", fontSize: 12, fontWeight: 600, "&:hover": { bgcolor: "#93000a" } }}
                    onClick={() => navigate(`/customer/dashboard/orders/${cancelNotification.orderId}`)}
                  >
                    View Order
                  </Button>
                )}
              </Box>
            </Alert>
          )}
          {activeSection === "dashboard" && (
            <>
              <Paper sx={{ borderRadius: 1, overflow: "hidden", boxShadow: "0 2px 4px rgba(0,0,0,0.08)" }}>
                <Box sx={{ px: { xs: 2, md: 3 }, py: 2, display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid", borderColor: colors.outlineVariant }}>
                  <Typography sx={{ fontWeight: 600, fontSize: 18, color: colors.onSurface }}>Recent Orders</Typography>
                  <Button onClick={() => navigate("/customer/dashboard/orders")} sx={{ color: colors.primary, fontWeight: 600, fontSize: 13, textTransform: "none" }}>View All Orders</Button>
                </Box>
                <Box sx={{ borderTop: "none" }}>
                  {!recentOrders.length ? (
                    <Box sx={{ p: 4, textAlign: "center" }}><Typography sx={{ color: colors.textMuted, fontSize: 14 }}>No orders yet</Typography></Box>
                  ) : (
                    recentOrders.map((order, index) => {
                      return (
                        <Box key={order._id}>
                          {(order.items || []).length
                            ? (order.items || []).map((item, i) => (
                                <Box key={`${order._id}-${item.productId || i}`}>
                                  <OrderItemRow item={item} order={order} navigate={navigate} />
                                  {i < (order.items || []).length - 1 && <Divider sx={{ borderColor: colors.outlineVariant, mx: { xs: 2, md: 3 } }} />}
                                </Box>
                              ))
                            : <OrderItemRow item={{ title: "Order Items" }} order={order} navigate={navigate} />}
                          {index < recentOrders.length - 1 && <Divider sx={{ borderColor: colors.outlineVariant, mx: { xs: 2, md: 3 } }} />}
                        </Box>
                      );
                    })
                  )}
                </Box>
              </Paper>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" }, gap: 2 }}>
                <UtilityCard icon={<LocationOnIcon sx={{ color: colors.primary }} />} title="Your Addresses" onClick={() => navigate("/register/customer")}>
                  <Box sx={{ p: 1.5, border: "2px solid #d9e2ff", borderRadius: 1, bgcolor: colors.surfaceContainerLow, mb: 2 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: 13, color: colors.textMain }}>Home (Default)</Typography>
                    <Typography sx={{ fontSize: 12, color: colors.onSurfaceVariant, mt: 0.5, lineHeight: 1.5 }}>{user?.firstName} {user?.lastName}<br />{user?.addresses?.[0]?.addressLine1 || "No address set"}</Typography>
                  </Box>
                  <Button fullWidth variant="outlined" onClick={() => navigate("/register/customer")} sx={{ borderColor: colors.outline, color: colors.primary, fontWeight: 600, fontSize: 13, textTransform: "none" }}>Edit Addresses</Button>
                </UtilityCard>
                <UtilityCard icon={<HistoryIcon sx={{ color: colors.secondaryContainer }} />} title="Browsing History" onClick={() => navigate("/customer/dashboard/browse-history")}>
                  <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1, mb: 2 }}>
                    {[0, 1, 2].map((i) => (
                      <Box key={i} sx={{ aspectRatio: "1/1", border: "1px solid", borderColor: colors.outlineVariant, borderRadius: 1, overflow: "hidden", bgcolor: colors.surfaceContainerLow, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        {recentlyViewed[i]?.imageUrl ? <Box component="img" src={recentlyViewed[i].imageUrl} alt="" sx={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <HistoryIcon sx={{ fontSize: 24, color: colors.textMuted }} />}
                      </Box>
                    ))}
                  </Box>
                  <Button fullWidth variant="outlined" onClick={() => navigate("/browse-history")} sx={{ borderColor: colors.outline, color: colors.onSurfaceVariant, fontWeight: 600, fontSize: 13, textTransform: "none" }}>View Full History</Button>
                </UtilityCard>
                <UtilityCard icon={<SupportAgentIcon sx={{ color: "#9a4100" }} />} title="Customer Service">
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 2 }}>
                    {[{ icon: <ReplayIcon sx={{ fontSize: 18 }} />, label: "Returns & Refunds" }, { icon: <PaymentsIcon sx={{ fontSize: 18 }} />, label: "Payment Issues" }, { icon: <SupportAgentIcon sx={{ fontSize: 18 }} />, label: "Live Chat Support" }].map((item) => (
                      <Box key={item.label} sx={{ display: "flex", alignItems: "center", gap: 1, cursor: "pointer", "&:hover": { color: colors.primary } }}>{item.icon}<Typography sx={{ fontSize: 13, color: "inherit" }}>{item.label}</Typography></Box>
                    ))}
                  </Box>
                  <Button fullWidth variant="contained" sx={{ bgcolor: colors.deepNavy, color: colors.onPrimary, fontWeight: 600, fontSize: 13, textTransform: "none", "&:hover": { opacity: 0.9 } }}>Contact Us</Button>
                </UtilityCard>
              </Box>
            </>
          )}
          {(activeSection === "orders" || activeSection === "manufacturing" || activeSection === "inTransit" || activeSection === "buyAgain" || activeSection === "delivered" || activeSection === "cancelled" || activeSection === "returned") && !orderId && <OrdersSection user={user} navigate={navigate} orderFilter={orderFilter} toggleWishlist={toggleWishlist} isWishlisted={isWishlisted} />}
          {activeSection === "wishlist" && <WishlistSection navigate={navigate} />}
          {activeSection === "browse-history" && <BrowseHistorySection navigate={navigate} />}
          {activeSection === "settings" && <ProfilePage fullWidth />}
          {activeSection === "support" && (
            <Paper sx={{ p: 3, borderRadius: 1, boxShadow: "0 2px 4px rgba(0,0,0,0.08)" }}>
              <Typography sx={{ fontWeight: 600, fontSize: 18, color: colors.onSurface, mb: 2 }}>Support</Typography>
              <Typography sx={{ color: colors.onSurfaceVariant, fontSize: 14, mb: 2 }}>Need help with an order or have a question? Our support team is here to help.</Typography>
              <Button variant="contained" fullWidth sx={{ bgcolor: colors.deepNavy, color: colors.onPrimary, textTransform: "none", mb: 3 }} onClick={() => window.dispatchEvent(new CustomEvent("open-chat"))}>Contact Support</Button>
              <Typography sx={{ fontWeight: 600, fontSize: 16, color: colors.onSurface, mb: 1.5 }}>Frequently Asked Questions</Typography>
              {[
                { q: "How do I track my order?", a: "Go to My Orders in your dashboard and click 'Track Order' on any order to see its current status and location." },
                { q: "How do I return an item?", a: "Visit My Orders, find the item you want to return, and request a return. You'll receive a return label and instructions via email." },
                { q: "How do I update my delivery address?", a: "Go to Account Settings in your dashboard and update your address details under the Address section." },
                { q: "How do I contact customer support?", a: "Click the 'Contact Support' button above or use the chat icon at the bottom right of the screen to chat with our support team." },
                { q: "How do I cancel an order?", a: "Orders can be cancelled before they enter manufacturing. Go to My Orders and check if the cancel option is available for your order." },
              ].map((faq, i) => (
                <Box key={i} sx={{ mb: 1.5, p: 1.5, borderRadius: 1, bgcolor: colors.surfaceContainerLow, border: "1px solid", borderColor: colors.outlineVariant }}>
                  <Typography sx={{ fontWeight: 600, fontSize: 13, color: colors.textMain, mb: 0.5 }}>{faq.q}</Typography>
                  <Typography sx={{ fontSize: 13, color: colors.onSurfaceVariant, lineHeight: 1.5 }}>{faq.a}</Typography>
                </Box>
              ))}
            </Paper>
          )}
          {orderId && <OrderDetailsDashboard />}
        </Box>
      </Box>
    </Box>
  );
};

export default CustomerDashboard;